import React, { useEffect, useState } from "react";
import Avatar from "../../components/ui/Avatar";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import weekScheduleService from "../../api/weekScheduleService";
import requestService from "../../api/requestService";
import { boardService, boardTaskService } from "../../api/taskBoardService";
import { axiosPrivate } from "../../api/axios";
import { formatDateLocal, parseDateLocal } from "../../utils/dateUtils";
import {
  isHoliday as isDayHoliday,
  getEgyptHolidayName,
} from "../../utils/holidays";
import {
  Users,
  Ticket,
  UserPlus,
  ListTodo,
  TicketPlus,
  FileText,
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlertTriangle,
  Plus,
  Kanban,
} from "lucide-react";
import { useDevRulesLock } from "../../context/DevRulesLockProvider";
import LiveOnDiscord from "../../components/presence/LiveOnDiscord";

// ---------------- Stat history (sparklines) ----------------
// There is no history API, so each point is reconstructed from timestamps:
// the value as it stood at the end of each of the last 8 weeks.
const HISTORY_WEEKS = 8;
const weekEnds = () => {
  const now = new Date();
  return Array.from({ length: HISTORY_WEEKS }, (_, i) => {
    const d = new Date(now);
    d.setDate(now.getDate() - (HISTORY_WEEKS - 1 - i) * 7);
    return d;
  });
};
const at = (v) => (v ? new Date(v) : null);
const isOpenTicket = (t) => t.status === "open" || t.status === "in_progress";

const buildHistory = ({ employees, tasks, tickets }) => {
  const ends = weekEnds();
  const series = (rows, fn) => (rows.length ? ends.map((end) => rows.filter((r) => fn(r, end)).length) : null);
  return {
    employees: series(employees, (e, end) => !at(e.createdAt) || at(e.createdAt) <= end),
    // Active at that time: created by then, planned, and not yet done by then.
    tasks: series(
      tasks,
      (t, end) =>
        at(t.createdAt) <= end &&
        t.status !== "product_backlog" &&
        !(t.status === "done" && at(t.updatedAt) <= end),
    ),
    // Open at that time: created by then and not closed before then.
    tickets: series(
      tickets,
      (t, end) => at(t.createdAt) <= end && (isOpenTicket(t) || at(t.updatedAt) > end),
    ),
  };
};

/** Area sparkline that draws itself in; colour comes from `currentColor`. */
const Sparkline = ({ points, id }) => {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const xy = points.map((v, i) => [(i / (points.length - 1)) * 100, max === min ? 22 : 36 - ((v - min) / span) * 30]);
  const line = xy.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full overflow-visible" aria-hidden="true">
      <defs>
        <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${line} L100,40 L0,40 Z`}
        fill={`url(#spark-${id})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      />
      <motion.path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
      />
    </svg>
  );
};

// ---------------- Hero terminal ----------------
const T = {
  arrow: "text-[#FF8A1F]",
  path: "text-[#5EE29A]",
  muted: "text-[#8C8883]",
};
const Prompt = ({ children }) => (
  <>
    <span className={T.arrow}>➜</span> <span className={T.path}>~/abs</span> {children}
  </>
);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Decorative terminal with live board numbers. Lines type in one by one
 * (120ms apart), then the cursor blinks. Chips only show real data.
 */
const HeroTerminal = ({ branch, done, active, dueToday, ready, sprintName, completionPct }) => {
  const lines = [
    <Prompt key="1">git checkout {branch}</Prompt>,
    <span key="2" className={T.muted}>Switched to branch &apos;{branch}&apos;</span>,
    <Prompt key="3">abs sprint status</Prompt>,
    <span key="4">
      <span className="text-[#5EE29A]">✓ {done} done</span>{"  "}
      <span className="text-[#FFB066]">● {active} active</span>{"  "}
      <span className="text-[#FF7B72]">! {dueToday} due today</span>
    </span>,
  ];
  const reduced = prefersReducedMotion();
  const [shown, setShown] = useState(reduced ? lines.length : 0);
  useEffect(() => {
    if (!ready || reduced) return undefined;
    setShown(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(i);
      if (i >= lines.length) clearInterval(id);
    }, 120);
    return () => clearInterval(id);
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps
  const typed = shown >= lines.length;

  return (
    <div
      aria-hidden="true"
      className="relative z-10 hero-float"
      style={{ flex: "0 1 420px", minWidth: "min(100%, 320px)" }}
    >
      <div
        className="rounded-2xl overflow-hidden backdrop-blur-xl text-[#E9E6E1]"
        style={{
          background: "rgba(12,12,13,.88)",
          border: "1px solid rgba(255,255,255,.1)",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.06)",
        }}
      >
        <div className="relative flex items-center gap-1.5 px-3.5 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,.07)" }}>
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <span className="absolute inset-x-0 text-center font-mono text-[11.5px] text-[#8C8883] pointer-events-none">abs-dev — zsh</span>
        </div>
        <div className="p-4 font-mono text-[12.5px] leading-[1.75] whitespace-pre overflow-hidden">
          {lines.map((l, i) => (
            <div key={i} style={{ visibility: i < shown ? "visible" : "hidden" }}>
              {l}
            </div>
          ))}
          <div style={{ visibility: typed ? "visible" : "hidden" }}>
            <Prompt>
              <span className="hero-cursor inline-block w-2 h-[15px] bg-[#FF8A1F]" style={{ verticalAlign: "-3px" }} />
            </Prompt>
          </div>
        </div>
      </div>

      {sprintName && (
        <span
          className="hero-float-a absolute -top-4 -left-3.5 z-20 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-line2 bg-panel-solid text-[12.5px] font-medium text-ink whitespace-nowrap"
          style={{ boxShadow: "var(--card-shadow), 0 12px 24px -12px rgba(0,0,0,.4)" }}
        >
          <span className="w-[7px] h-[7px] rounded-full bg-ok" style={{ boxShadow: "0 0 8px var(--green)" }} />
          {sprintName} · active
        </span>
      )}
      {completionPct !== null && (
        <span className="hero-float-b absolute -bottom-3.5 -right-2.5 z-20 inline-flex items-center px-2.5 py-1.5 rounded-[10px] bg-grad text-on-accent shadow-glow font-mono text-[12px] font-semibold whitespace-nowrap">
          ✓ {completionPct}% done this sprint
        </span>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { lockState } = useDevRulesLock();
  const employee = auth?.employee;

  const [mySchedule, setMySchedule] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeTasks: 0,
    openTickets: 0,
    completedTasksThisWeek: 0,
    incompletedTasksThisWeek: 0,
    dueToday: 0,
  });
  // Weekly history (oldest → newest) derived from record timestamps.
  const [history, setHistory] = useState({});
  const [loadingStats, setLoadingStats] = useState(true);
  const [todayRequests, setTodayRequests] = useState([]);
  const [loadingToday, setLoadingToday] = useState(true);
  // Everyone visible in this week's schedule with today's status (current week only).
  const [todayTeam, setTodayTeam] = useState(null);
  const [activeSprintName, setActiveSprintName] = useState(null);
  const [activeSprintId, setActiveSprintId] = useState(null);
  const [boardTasks, setBoardTasks] = useState([]);

  const isAdminUser = isAdmin(employee);

  // Get current week start (Sunday)
  const getCurrentWeekStart = () => {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    sunday.setHours(0, 0, 0, 0);
    return sunday;
  };

  // Format date to YYYY-MM-DD in local time (for API)
  const formatDateForAPI = (date) => formatDateLocal(date);

  // Format date range display (weekStart is a Date in local time)
  const formatDateRange = (weekStart) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} → ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  // Fetch my schedule from Week Schedule API
  const fetchMySchedule = async (weekStart) => {
    try {
      setLoadingSchedule(true);
      const weekStartStr = formatDateForAPI(weekStart);
      const response = await weekScheduleService.getWeekSchedule(weekStartStr);

      // Find current user's schedule
      if (response.data && response.data.employees) {
        const myData = response.data.employees.find(
          (emp) =>
            emp.employeeId === employee._id || emp.employeeId === employee.id,
        );

        if (myData) {
          setMySchedule({
            days: response.data.days,
            myDays: myData.days,
          });
        } else {
          setMySchedule({
            days: response.data.days,
            myDays: {},
          });
        }

        {
          const now = new Date();
          const thisSunday = new Date(now);
          thisSunday.setDate(now.getDate() - now.getDay());
          thisSunday.setHours(0, 0, 0, 0);
          if (weekStart.getTime() === thisSunday.getTime()) {
            const todayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][now.getDay()];
            setTodayTeam(
              response.data.employees.map((emp) => ({
                id: emp.employeeId,
                name: emp.employeeName || emp.employeeNameAr || "Unknown",
                team: emp.team,
                photo: emp.employeePhoto || null,
                status: emp.days[todayKey] || "Office",
              })),
            );
          }
        }

      }
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  // Fetch dashboard stats (per role: admin vs team user)
  const fetchStats = async () => {
    try {
      setLoadingStats(true);

      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      let totalEmployees = 0;
      let openTickets = 0;
      let employeeRows = [];
      let ticketRows = [];

      // Task stats come from the unified task board. The API already limits
      // them to what this user may see (admins: all; squad leads: their
      // squads; members: their own tasks).
      const tasksPromise = boardTaskService
        .list()
        .then((res) => res.data || [])
        .catch(() => []);
      const inThisWeek = (date) => {
        if (!date) return false;
        const d = new Date(date);
        return d >= startOfWeek && d <= endOfWeek;
      };

      if (isAdminUser) {
        // Admin: Total Employees, all open tickets
        const [employeesRes, ticketsRes] = await Promise.all([
          axiosPrivate.get("/api/employees"),
          axiosPrivate.get("/api/tickets"),
        ]);

        const employeesData =
          employeesRes.data?.data ||
          employeesRes.data?.employees ||
          employeesRes.data ||
          [];
        totalEmployees = Array.isArray(employeesData)
          ? employeesData.length
          : 0;
        employeeRows = Array.isArray(employeesData) ? employeesData : [];

        const ticketsData =
          ticketsRes.data?.tickets ??
          ticketsRes.data?.data ??
          (Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
        ticketRows = Array.isArray(ticketsData) ? ticketsData : [];
        openTickets = Array.isArray(ticketsData)
          ? ticketsData.filter(
              (t) => t.status === "open" || t.status === "in_progress",
            ).length
          : 0;
      } else {
        // Non-admin: tickets assigned to current user only
        const myId = employee?._id || employee?.id;
        const ticketsRes = await axiosPrivate
          .get("/api/tickets")
          .catch(() => ({ data: { tickets: [] } }));

        const ticketsData =
          ticketsRes.data?.tickets ??
          ticketsRes.data?.data ??
          (Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
        const ticketsList = Array.isArray(ticketsData) ? ticketsData : [];
        const mine = ticketsList.filter((t) =>
          (t.assignedTo || []).some(
            (a) => (a?._id ?? a)?.toString() === (myId ?? "")?.toString(),
          ),
        );
        ticketRows = mine;
        openTickets = mine.filter(
          (t) => t.status === "open" || t.status === "in_progress",
        ).length;
      }

      const tasks = await tasksPromise;
      setBoardTasks(tasks);
      // Active: committed work that is not finished (product backlog excluded).
      const activeTasks = tasks.filter(
        (t) => t.status !== "product_backlog" && t.status !== "done",
      ).length;
      const completedTasksThisWeek = tasks.filter(
        (t) => t.status === "done" && inThisWeek(t.updatedAt),
      ).length;
      // Created this week and still not started (sprint backlog / ready).
      const incompletedTasksThisWeek = tasks.filter(
        (t) =>
          (t.status === "sprint_backlog" || t.status === "ready") &&
          inThisWeek(t.createdAt),
      ).length;

      const todayStr = new Date().toDateString();
      const dueToday = tasks.filter(
        (t) => t.status !== "done" && t.dueDate && new Date(t.dueDate).toDateString() === todayStr,
      ).length;

      setHistory(
        buildHistory({ employees: employeeRows, tasks, tickets: ticketRows }),
      );
      setStats({
        totalEmployees,
        activeTasks,
        openTickets,
        completedTasksThisWeek,
        incompletedTasksThisWeek,
        dueToday,
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Fetch today's requests - fetch team requests for same-team employees
  const fetchTodayRequests = async () => {
    try {
      setLoadingToday(true);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Only approved requests that cover today – filtered server-side so we
      // never pull the whole requests collection just to show today's rows.
      const params = { status: "approved", activeOn: today.toISOString() };

      // Fetch requests: admin gets all, non-admin gets their team's requests
      let response;
      if (isAdminUser) {
        response = await requestService.getRequests(params);
      } else if (employee?.team) {
        response = await requestService.getRequests({
          ...params,
          team: employee.team,
        });
      } else {
        // Fallback: own requests only
        response = await requestService.getRequests(params);
      }

      const allRequests = response?.data ?? response ?? [];
      const list = Array.isArray(allRequests) ? allRequests : [];

      // Filter to approved requests that cover today
      let todaysRequests = list.filter((req) => {
        if (req.status !== "approved") return false;
        const startDate = new Date(req.startDate);
        const endDate = new Date(req.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        return today >= startDate && today <= endDate;
      });
      // Non-admin: restrict to same team; admin: show all employees
      if (!isAdminUser) {
        const userTeam = String(employee?.team || "").trim();
        todaysRequests = todaysRequests.filter((req) => {
          const reqTeam = String(req.employee?.team || "").trim();
          return reqTeam && userTeam && reqTeam === userTeam;
        });
      }

      setTodayRequests(todaysRequests);
    } catch (error) {
      console.error("Failed to fetch today requests:", error);
      setTodayRequests([]);
    } finally {
      setLoadingToday(false);
    }
  };

  // Initial load
  useEffect(() => {
    const weekStart = getCurrentWeekStart();
    setCurrentWeekStart(weekStart);
    fetchMySchedule(weekStart);
    fetchStats();
    fetchTodayRequests();
    boardService
      .getOptions()
      .then((res) => {
        const active = (res.data?.sprints || []).find((sp) => sp.status === "active");
        setActiveSprintName(active?.name || null);
        setActiveSprintId(active?._id || null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate week
  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
    fetchMySchedule(newWeekStart);
  };

  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
    fetchMySchedule(newWeekStart);
  };

  const handleCurrentWeek = () => {
    const weekStart = getCurrentWeekStart();
    setCurrentWeekStart(weekStart);
    fetchMySchedule(weekStart);
  };

  // Check if currently viewing current week
  const isViewingCurrentWeek = () => {
    if (!currentWeekStart) return false;
    const now = getCurrentWeekStart();
    return currentWeekStart.getTime() === now.getTime();
  };

  // Quick Actions handlers
  const handleAddEmployee = () => {
    navigate("/management/employees", { state: { openAddModal: true } });
  };

  const handleCreateTask = () => {
    navigate("/task-board", { state: { openCreateTask: true } });
  };

  const handleNewTicket = () => {
    navigate("/communication/tickets");
  };

  const handleAddRequest = () => {
    navigate("/management/requests", { state: { openAddModal: true } });
  };

  // Keyboard shortcuts for the quick actions (ignored while typing).
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const el = e.target;
      if (el?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el?.tagName)) return;
      if (document.querySelector('[role="dialog"]')) return;
      const k = e.key.toLowerCase();
      if (k === "e" && isAdminUser) handleAddEmployee();
      else if (k === "t") handleCreateTask();
      else if (k === "k") handleNewTicket();
      else if (k === "r") handleAddRequest();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminUser]);

  // Render day cell
  const renderDayCell = (day, index) => {
    if (!mySchedule) return null;

    const dayKey = day.dayName.toLowerCase().substring(0, 3);
    const isHoliday = isDayHoliday(day.date, day.dayName);
    const holidayName = getEgyptHolidayName(day.date);
    const dayValue = mySchedule.myDays[dayKey] || "Office";
    const isWfhMissionVacation = ["WFH", "Mission", "Vacation"].includes(dayValue);
    const today = new Date();
    const dayDate = parseDateLocal(day.date);
    const isToday = !isNaN(dayDate.getTime()) && dayDate.toDateString() === today.toDateString();

    let displayLabel = dayValue;
    if (isHoliday && dayValue === "Office") displayLabel = holidayName || "Holiday";
    const holidayOnly = isHoliday && dayValue === "Office";

    return (
      <div
        key={index}
        title={displayLabel}
        className={`relative min-w-0 px-1 py-3 sm:py-5 rounded-2xl border text-center transition-all duration-200 overflow-hidden ${
          isToday
            ? "bg-grad border-transparent shadow-glow -translate-y-1 text-on-accent"
            : holidayOnly
              ? "border-line text-danger"
              : isWfhMissionVacation
                ? "bg-accent-soft border-accent-line text-accent-ink hover:-translate-y-0.5"
                : "bg-panel2 border-line text-ink hover:-translate-y-0.5 hover:border-line2"
        }`}
        style={
          holidayOnly && !isToday
            ? {
                backgroundImage:
                  "repeating-linear-gradient(135deg, var(--red-soft) 0 9px, transparent 9px 18px)",
              }
            : undefined
        }
      >
        <p className={`text-[10px] sm:text-[11.5px] font-medium tracking-[0.06em] ${isToday ? "" : holidayOnly ? "" : "text-muted"}`}>
          {day.dayName.substring(0, 3).toUpperCase()}
        </p>
        <p className="font-display text-lg sm:text-[30px] leading-tight font-semibold my-0.5 sm:my-1.5">
          {day.dayNumber}
        </p>
        <p
          className={`text-[9px] sm:text-[12px] font-medium leading-tight truncate px-0.5 ${
            isToday ? "opacity-80" : holidayOnly ? "" : isWfhMissionVacation ? "" : "text-muted"
          }`}
        >
          {displayLabel}
        </p>
      </div>
    );
  };

  const firstName = (employee?.fullNameEn || employee?.nickname || "").split(/\s+/)[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const now = new Date();
  const todayStr = formatDateForAPI(now);
  const todayDayName = now.toLocaleDateString("en-US", { weekday: "long" });
  const todayIsHoliday = isDayHoliday(todayStr, todayDayName);
  const myTodayStatus = todayIsHoliday
    ? "Holiday"
    : (todayTeam || []).find((m) => String(m.id) === String(employee?._id || employee?.id))?.status || null;
  const dateChip = [
    now.toLocaleDateString("en-US", { weekday: "short" }),
    now.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    myTodayStatus === "Office" ? "In office" : myTodayStatus,
  ]
    .filter(Boolean)
    .join(" · ")
    .toUpperCase();

  const contextLine = loadingStats
    ? "Loading your overview…"
    : stats.dueToday > 0
      ? `${stats.dueToday} task${stats.dueToday === 1 ? " is" : "s are"} due today · ${stats.activeTasks} active on the board.`
      : `${stats.activeTasks} active task${stats.activeTasks === 1 ? "" : "s"} on the board · nothing due today.`;

  const branch = activeSprintName
    ? `sprint-${activeSprintName.toLowerCase().replace(/^sprint\s*/, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
    : "main";
  // Active sprint completion: done vs all tasks in the sprint (that the user can see).
  const sprintTasks = activeSprintId
    ? boardTasks.filter((t) => String(t.sprint?._id ?? t.sprint) === String(activeSprintId))
    : [];
  const doneWeek = sprintTasks.filter((t) => t.status === "done").length;
  const leftWeek = sprintTasks.length - doneWeek;
  const completionPct = sprintTasks.length ? Math.round((doneWeek / sprintTasks.length) * 100) : 0;

  const statCards = [
    ...(isAdminUser
      ? [{ id: "emp", Icon: Users, label: "Total employees", value: stats.totalEmployees, tone: "info", series: history.employees, line: "text-sky-400" }]
      : []),
    { id: "tasks", Icon: Kanban, label: "Active tasks", value: stats.activeTasks, tone: "accent", series: history.tasks, line: "text-accent" },
    { id: "tickets", Icon: Ticket, label: "Open tickets", value: stats.openTickets, tone: "warn", series: history.tickets, line: "text-violet-400" },
  ];
  const toneTile = {
    info: "bg-sky-400/10 text-sky-400",
    accent: "bg-accent-soft text-accent-ink",
    warn: "bg-violet-400/10 text-violet-400",
  };

  const quickActions = [
    ...(isAdminUser ? [{ Icon: UserPlus, label: "Add employee", sub: "New team member", kbd: "E", onClick: handleAddEmployee }] : []),
    { Icon: ListTodo, label: "Create task", sub: "On the sprint board", kbd: "T", onClick: handleCreateTask },
    { Icon: TicketPlus, label: "New ticket", sub: "Report an issue", kbd: "K", onClick: handleNewTicket },
    { Icon: FileText, label: "Add request", sub: "WFH, leave, mission", kbd: "R", onClick: handleAddRequest },
  ];

  // Today's team: status pill per person.
  const statusPill = (status) => {
    if (status === "Office") return { cls: "pill-ok", label: "In office" };
    if (status === "WFH") return { cls: "pill-accent", label: "Remote" };
    if (status === "Vacation") return { cls: "pill-danger", label: "On leave" };
    return { cls: "pill-warn", label: status };
  };
  const team = todayTeam || [];
  const inOffice = team.filter((m) => m.status === "Office").length;
  const remote = team.filter((m) => m.status === "WFH").length;
  const onLeave = team.filter((m) => m.status === "Vacation").length;
  const vacationUntil = new Map(
    todayRequests
      .filter((r) => r.requestType === "vacation" && r.endDate)
      .map((r) => [String(r.employee?._id), new Date(r.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })]),
  );
  const teamLabel = (t) =>
    ({ high_potit: "High Potit", backend: "Backend", frontend: "Frontend", tester: "Tester", ra: "Requirement Analyst", mobile_app: "Mobile App", uiux: "UI/UX" })[t] || t || "";

  return (
    <PageWrapper>
      {lockState?.locked && (
        <div className="ws-in mb-6 rounded-2xl border border-warn/40 bg-warn-soft px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-warn">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">New Dev Rules update is pending your review.</span>
          </div>
          <button onClick={() => navigate("/main/dev-rules")} className="text-sm font-semibold text-accent-ink hover:text-accent">
            Open Rules
          </button>
        </div>
      )}

      {/* Hero */}
      <section
        className="hero-in relative overflow-hidden rounded-[26px] border border-line shadow-card mb-6 p-6 sm:p-9 pb-10 flex flex-wrap items-center justify-between gap-10"
        style={{ background: "var(--grad-soft), var(--panel)" }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 -bottom-32 w-[420px] h-[420px] rounded-full opacity-60 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--accent-soft), transparent 70%)" }}
        />
        <div className="relative flex-1 min-w-[280px]">
          <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-line2 bg-panel font-mono text-[12px] text-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse" />
            {dateChip}
          </span>
          <h1 className="mt-4 font-display text-[32px] sm:text-[44px] leading-[1.05] font-semibold tracking-[-0.035em] text-ink">
            {greeting}, <span className="grad-text">{firstName}</span>
          </h1>
          <p className="mt-3 text-[15px] text-muted">{contextLine}</p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <button onClick={handleCreateTask} className="btn-primary">
              <Plus className="w-4 h-4" /> Create task
            </button>
            <button onClick={handleAddRequest} className="btn-ghost">
              New request
            </button>
          </div>
        </div>

        <HeroTerminal
          branch={branch}
          done={loadingStats ? "…" : doneWeek}
          active={loadingStats ? "…" : stats.activeTasks}
          dueToday={loadingStats ? "…" : stats.dueToday}
          ready={!loadingStats}
          sprintName={activeSprintName}
          completionPct={loadingStats || !activeSprintId ? null : completionPct}
        />
      </section>

      {/* My schedule */}
      <section className="ws-in card !p-5 sm:!p-6 mb-6" style={{ "--ws-delay": "60ms" }}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <h3 className="font-display text-[17px] font-semibold text-ink whitespace-nowrap">My schedule</h3>
            <p className="text-[13px] text-faint mt-0.5">{currentWeekStart && formatDateRange(currentWeekStart)}</p>
          </div>
          {!isViewingCurrentWeek() && (
            <button onClick={handleCurrentWeek} disabled={loadingSchedule} className="btn-ghost !h-9 text-[13px]">
              Current week
            </button>
          )}
        </div>

        {loadingSchedule && !mySchedule ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-line2 border-t-accent" />
          </div>
        ) : (
          <div className={`flex items-stretch gap-1.5 sm:gap-2.5 transition-opacity ${loadingSchedule ? "opacity-60" : ""}`}>
            <button
              onClick={handlePreviousWeek}
              disabled={loadingSchedule}
              aria-label="Previous week"
              className="shrink-0 w-7 sm:w-10 flex items-center justify-center rounded-2xl border border-line bg-panel2 text-muted hover:text-ink hover:bg-hover transition-colors disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0 grid grid-cols-7 gap-1 sm:gap-2.5 pt-1">
              {mySchedule && mySchedule.days.map((day, idx) => renderDayCell(day, idx))}
            </div>
            <button
              onClick={handleNextWeek}
              disabled={loadingSchedule}
              aria-label="Next week"
              className="shrink-0 w-7 sm:w-10 flex items-center justify-center rounded-2xl border border-line bg-panel2 text-muted hover:text-ink hover:bg-hover transition-colors disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* Stats */}
      <section className="grid gap-4 mb-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {statCards.map((c, i) => (
          <div
            key={c.id}
            className="ws-in card !p-5 relative overflow-hidden hover:-translate-y-0.5"
            style={{ "--ws-delay": `${120 + i * 60}ms` }}
          >
            {(() => {
              const pts = !loadingStats && c.series && c.series.length > 1 ? c.series : null;
              const delta = pts ? pts[pts.length - 1] - pts[pts.length - 2] : 0;
              return (
                <>
                  {pts && Math.max(...pts) > 0 && (
                    <div className={`absolute right-0 bottom-0 w-[58%] h-[58px] pointer-events-none ${c.line}`}>
                      <Sparkline points={pts} id={c.id} />
                    </div>
                  )}
                  {delta !== 0 && (
                    <span
                      className={`absolute top-5 right-5 font-mono text-[12.5px] font-medium ${delta > 0 ? "text-ok" : "text-danger"}`}
                      title="Change since last week"
                    >
                      {delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`}
                    </span>
                  )}
                </>
              );
            })()}
            <span className={`relative w-10 h-10 rounded-xl flex items-center justify-center ${toneTile[c.tone]}`}>
              <c.Icon className="w-[18px] h-[18px]" />
            </span>
            <p className="relative mt-4 font-display text-[34px] leading-none font-semibold text-ink">{loadingStats ? "…" : c.value}</p>
            <p className="relative mt-2 text-[13.5px] text-muted whitespace-nowrap">{c.label}</p>
          </div>
        ))}
        <div className="ws-in card !p-5 hover:-translate-y-0.5" style={{ "--ws-delay": `${120 + statCards.length * 60}ms` }}>
          <p className="text-[13.5px] text-muted whitespace-nowrap truncate">{activeSprintName ? `${activeSprintName} completion` : "Sprint completion"}</p>
          <div className="mt-3 flex items-center gap-5">
            <svg width="84" height="84" viewBox="0 0 84 84" className="shrink-0 -rotate-90">
              <circle cx="42" cy="42" r="35" fill="none" stroke="var(--line2)" strokeWidth="9" />
              <motion.circle
                cx="42"
                cy="42"
                r="35"
                fill="none"
                stroke="var(--green)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 35}
                initial={{ strokeDashoffset: 2 * Math.PI * 35 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 35 * (1 - (loadingStats ? 0 : completionPct) / 100) }}
                transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
              />
            </svg>
            <div className="min-w-0">
              <p className="font-display text-[34px] leading-none font-semibold text-ink">{loadingStats ? "…" : `${completionPct}%`}</p>
              <p className="mt-1.5 text-[13px] text-muted whitespace-nowrap">
                {activeSprintId ? `${doneWeek} done · ${leftWeek} left` : "No active sprint"}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Discord presence (whole team) */}
      <LiveOnDiscord />

      {/* Today's team & Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <section className="ws-in card !p-5 sm:!p-6" style={{ "--ws-delay": "300ms" }}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display text-[17px] font-semibold text-ink whitespace-nowrap">Today&apos;s team</h3>
              <p className="text-[13px] text-faint mt-0.5">
                {todayIsHoliday
                  ? getEgyptHolidayName(todayStr) || "Holiday"
                  : `${inOffice} in office · ${remote} remote · ${onLeave} on leave`}
              </p>
            </div>
            <button onClick={() => navigate("/main/week-schedule")} className="btn-primary !h-9 text-[13px]">
              Week schedule
            </button>
          </div>

          {todayIsHoliday ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <span className="w-12 h-12 rounded-2xl bg-danger-soft text-danger flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </span>
              <p className="text-sm text-muted">Today is a holiday — no one in office</p>
            </div>
          ) : !todayTeam && (loadingSchedule || loadingToday) ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-line2 border-t-accent" />
            </div>
          ) : team.length === 0 ? (
            <p className="text-center py-8 text-sm text-faint">No schedule entries today</p>
          ) : (
            <>
              <ul className="space-y-2 max-h-[360px] overflow-y-auto pr-1 -mr-1">
                {[...team]
                  .sort((a, b) => (a.status === "Office") - (b.status === "Office") || a.name.localeCompare(b.name))
                  .map((m, i) => {
                    const pill = statusPill(m.status);
                    const until = m.status === "Vacation" ? vacationUntil.get(String(m.id)) : null;
                    return (
                      <li key={m.id || i} className="flex items-center gap-3 p-2.5 rounded-2xl border border-line bg-panel2 hover:bg-hover transition-colors">
                        <Avatar src={m.photo} name={m.name} shape="rounded" className="!w-[38px] !h-[38px] !text-[13px]" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[14px] font-medium text-ink truncate">{m.name}</p>
                          <p className="text-[12px] text-faint truncate">
                            {[teamLabel(m.team), until && `until ${until}`].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        <span className={`pill ${pill.cls}`}>{pill.label}</span>
                      </li>
                    );
                  })}
              </ul>
              {team.length > 0 && (
                <div className="mt-5">
                  <div className="flex items-center justify-between text-[13px] mb-2">
                    <span className="text-muted">Office capacity</span>
                    <span className="font-mono font-medium text-ink">
                      {inOffice} / {team.length}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-raise overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-grad shadow-glow"
                      initial={{ width: 0 }}
                      animate={{ width: `${(inOffice / team.length) * 100}%` }}
                      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section className="ws-in card !p-5 sm:!p-6" style={{ "--ws-delay": "360ms" }}>
          <h3 className="font-display text-[17px] font-semibold text-ink mb-4 whitespace-nowrap">Quick actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickActions.map((a) => (
              <button
                key={a.label}
                onClick={a.onClick}
                className="group flex items-center gap-3 p-3.5 rounded-2xl border border-line bg-panel2 text-left hover:bg-hover hover:border-accent-line hover:-translate-y-0.5 transition-all"
              >
                <span className="shrink-0 w-10 h-10 rounded-xl bg-grad shadow-glow text-on-accent flex items-center justify-center">
                  <a.Icon className="w-[18px] h-[18px]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-medium text-ink whitespace-nowrap">{a.label}</span>
                  <span className="block text-[12px] text-faint truncate">{a.sub}</span>
                </span>
                <kbd className="shrink-0 w-6 h-6 rounded-md border border-line2 text-[11px] text-faint flex items-center justify-center">
                  {a.kbd}
                </kbd>
              </button>
            ))}
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default Dashboard;
